import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Calendar as CalIcon, TrendingUp } from 'lucide-react';
import { useCalendarStore } from '../../../state/calendarStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import ExportButton from '../../../components/ui/ExportButton';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const ACTION_COLORS = {
  dividend: 'bg-emerald-500',
  split: 'bg-blue-500',
  bonus: 'bg-violet-500',
  rights: 'bg-amber-500',
  default: 'bg-slate-400',
};

function getActionColor(type) {
  if (!type) return ACTION_COLORS.default;
  const t = type.toLowerCase();
  if (t.includes('dividend')) return ACTION_COLORS.dividend;
  if (t.includes('split')) return ACTION_COLORS.split;
  if (t.includes('bonus')) return ACTION_COLORS.bonus;
  if (t.includes('right')) return ACTION_COLORS.rights;
  return ACTION_COLORS.default;
}

function buildCalendarGrid(year, month) {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const rows = [];
  let day = 1 - firstDay;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++, day++) {
      week.push(day >= 1 && day <= daysInMonth ? day : null);
    }
    if (week.some(Boolean)) rows.push(week);
  }
  return rows;
}

function DayCell({ day, events, isToday, onClick }) {
  if (day === null) return <div className="min-h-[72px]" />;
  const max = 3;
  return (
    <div
      className={`min-h-[72px] border border-border/30 rounded-lg p-1.5 cursor-pointer transition-colors hover:bg-muted/40 ${
        isToday ? 'ring-2 ring-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-950/20' : ''
      }`}
      onClick={() => events.length > 0 && onClick(day)}
    >
      <span className={`text-xs font-medium ${isToday ? 'text-emerald-600 font-bold' : 'text-muted-foreground'}`}>
        {day}
      </span>
      <div className="mt-0.5 space-y-0.5">
        {events.slice(0, max).map((e, i) => (
          <div key={i} className="flex items-center gap-1 truncate">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${e.color}`} />
            <span className="text-[10px] leading-tight truncate">{e.label}</span>
          </div>
        ))}
        {events.length > max && (
          <span className="text-[10px] text-muted-foreground">+{events.length - max} more</span>
        )}
      </div>
    </div>
  );
}

function EventDetail({ events, onClose, tab }) {
  if (!events || events.length === 0) return null;
  return (
    <Card className="mt-4">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Events on this day</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {events.map((e, i) => (
          <div key={i} className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`w-2 h-2 rounded-full shrink-0 ${e.color}`} />
              <span className="font-semibold text-sm">{e.company}</span>
              <Badge variant="outline" className="text-[10px]">{e.symbol}</Badge>
              {tab === 'corp-actions' && (
                <Badge className={`text-[10px] text-white ${e.color}`}>{e.type}</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{e.detail}</p>
            {e.amount != null && (
              <p className="text-xs font-medium mt-0.5">Amount: ₹{e.amount}</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const {
    corpActions, earnings, loading, error,
    month, year, activeTab,
    setMonth, setActiveTab, fetchCorpActions, fetchEarnings,
  } = useCalendarStore();

  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (activeTab === 'earnings') fetchEarnings(month, year);
    else fetchCorpActions(month, year);
  }, []);

  const events = activeTab === 'corp-actions' ? corpActions : earnings;

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const dateStr = activeTab === 'corp-actions' ? e.ex_date : e.date;
      if (!dateStr) continue;
      const d = new Date(dateStr).getDate();
      if (!map[d]) map[d] = [];
      if (activeTab === 'corp-actions') {
        map[d].push({
          label: e.symbol || e.company_name,
          color: getActionColor(e.action_type),
          company: e.company_name,
          symbol: e.symbol,
          type: e.action_type,
          detail: e.purpose || e.details || '',
          amount: e.amount,
        });
      } else {
        map[d].push({
          label: e.symbol || e.company_name,
          color: 'bg-red-500',
          company: e.company_name,
          symbol: e.symbol,
          type: 'Earnings',
          detail: e.headline || e.category || '',
          amount: null,
        });
      }
    }
    return map;
  }, [events, activeTab]);

  const grid = useMemo(() => buildCalendarGrid(year, month), [year, month]);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

  const prev = () => {
    const m = month === 1 ? 12 : month - 1;
    const y = month === 1 ? year - 1 : year;
    setSelectedDay(null);
    setMonth(m, y);
  };
  const next = () => {
    const m = month === 12 ? 1 : month + 1;
    const y = month === 12 ? year + 1 : year;
    setSelectedDay(null);
    setMonth(m, y);
  };

  const selectedEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  const actionTypes = useMemo(() => {
    if (activeTab !== 'corp-actions') return [];
    const types = new Set(corpActions.map(e => e.action_type).filter(Boolean));
    return [...types].sort();
  }, [corpActions, activeTab]);

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <CalIcon className="w-5 h-5 text-emerald-500" /> Market Calendar
          </h1>
          <p className="text-xs text-muted-foreground">Corporate actions, dividends, earnings dates</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border">
        {[
          { key: 'corp-actions', label: 'Corporate Actions', icon: CalIcon },
          { key: 'earnings', label: 'Earnings', icon: TrendingUp },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setSelectedDay(null); setActiveTab(tab.key); }}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            <span className="ml-1 text-[10px] text-muted-foreground">({events.length})</span>
          </button>
        ))}
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={prev}><ChevronLeft className="w-4 h-4" /></Button>
        <h2 className="text-lg font-semibold">{MONTHS[month - 1]} {year}</h2>
        <div className="flex items-center gap-2">
          <ExportButton rows={events} filename={`calendar-${activeTab}-${year}-${month}`} />
          <Button variant="ghost" size="icon" onClick={next}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Legend */}
      {activeTab === 'corp-actions' && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(ACTION_COLORS).filter(([k]) => k !== 'default').map(([key, color]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
              <span className="capitalize text-muted-foreground">{key}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <>
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px">
            {DAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground uppercase py-2">
                {d}
              </div>
            ))}
            {grid.flatMap((week, wi) =>
              week.map((day, di) => (
                <DayCell
                  key={`${wi}-${di}`}
                  day={day}
                  events={day ? (eventsByDay[day] || []) : []}
                  isToday={isCurrentMonth && day === today.getDate()}
                  onClick={setSelectedDay}
                />
              ))
            )}
          </div>

          <EventDetail events={selectedEvents} onClose={() => setSelectedDay(null)} tab={activeTab} />

          {events.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">
              No {activeTab === 'earnings' ? 'earnings' : 'corporate action'} events found for {MONTHS[month - 1]} {year}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
